import { useState, useEffect } from 'react';
import createAxiosInstance from '../utils/axiosInstance';
import Env from '../env/Env';

export interface DashboardView {
  id: number;
  name: string;
  description: string;
  order: number;
  isActive: number;
  createdAt?: string;
  updatedAt?: string;
  graphs?: any[];
  filters?: any[];
}

export interface DashboardViewsResponse {
  success: boolean;
  data: {
    mainDashboards: DashboardView[];
    adminDashboard?: DashboardView;
    managerDashboard?: DashboardView;
  };
}

const request = createAxiosInstance(`${Env.API_BASE}/api/dashboard-views`);

const dashboardViewsAPIsService = {
  fetchAllDashboardViews: (signal?: AbortSignal): Promise<DashboardViewsResponse> => {
    return new Promise((resolve, reject) => {
      request
        .get("/all", { signal })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || []);
        });
    });
  },

  updateDashboardOrder: (updates: Array<{id: number, order: number}>): Promise<any> => {
    return new Promise((resolve, reject) => {
      request
        .put("/update-order", { updates })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to update dashboard order');
        });
    });
  },

  updateDashboardName: (id: number, name: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      request
        .put(`/${id}/update-name`, { name })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to update dashboard name');
        });
    });
  },

  updateDashboardStatus: (id: number, isActive: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      request
        .put(`/${id}/update-status`, { isActive })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to update dashboard status');
        });
    });
  },

  restoreDefaultLayout: (): Promise<any> => {
    return new Promise((resolve, reject) => {
      request
        .post("/restore-defaults")
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to restore default layout');
        });
    });
  },

  resetDashboardData: (dashboardId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      request
        .post(`/${dashboardId}/reset`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to reset dashboard data');
        });
    });
  },

  updatePageGraphs: (dashboardId: number, graphs: any[], filters: any[]): Promise<any> => {
    return new Promise((resolve, reject) => {
      request
        .put(`/${dashboardId}/update-graphs`, { graphs, filters })
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err?.response?.data?.errors || err?.response?.data?.message || 'Failed to update page graphs');
        });
    });
  },
};

export const useDashboardViews = (showAll: boolean = false, includeAdminManager: boolean = false) => {
  const [dashboardViews, setDashboardViews] = useState<DashboardView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const fetchDashboardViews = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await dashboardViewsAPIsService.fetchAllDashboardViews();

      if (response.success) {
        // Start with main dashboards
        let filteredDashboards = response.data.mainDashboards || [];

        // Optionally include admin and manager dashboards (respect isActive unless showAll)
        if (includeAdminManager) {
          if (response.data.adminDashboard && (showAll || response.data.adminDashboard.isActive === 1)) {
            filteredDashboards = [...filteredDashboards, response.data.adminDashboard];
          }
          if (response.data.managerDashboard && (showAll || response.data.managerDashboard.isActive === 1)) {
            filteredDashboards = [...filteredDashboards, response.data.managerDashboard];
          }
        }
        
        if (!showAll) {
          // Filter only active main dashboards
          filteredDashboards = filteredDashboards.filter((dashboard: DashboardView) => dashboard.isActive === 1);
        }
        
        // Sort by order
        const sortedDashboards = filteredDashboards.sort((a: DashboardView, b: DashboardView) => a.order - b.order);

        setDashboardViews(sortedDashboards);
      } else {
        setError('Failed to fetch dashboard views');
      }
    } catch (err) {
      console.error('Error fetching dashboard views:', err);
      setError('Failed to fetch dashboard views');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardViews();
  }, [refetchTrigger, showAll, includeAdminManager]);

  return {
    dashboardViews,
    loading,
    error,
    refetch: () => {
      setRefetchTrigger(prev => prev + 1);
    }
  };
};

export { dashboardViewsAPIsService };
